import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ServiceCard from './components/ServiceCard';
import ProcessFlow from './components/ProcessFlow';
import ServiceModal from './components/ServiceModal';
import PricingSection from './components/PricingSection';
import TestimonialCarousel from './components/TestimonialCarousel';
import FAQSection from './components/FAQSection';

const ServicesOverview = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedService, setSelectedService] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const services = [
  {
    id: 1,
    title: "Talent Management",
    icon: "Users",
    gradientClass: "bg-gradient-to-br from-primary to-secondary",
    description: "Comprehensive career management for African football talent seeking European opportunities with personalized development plans and strategic positioning.",
    fullDescription: "Our talent management service provides end-to-end career support for African football players aspiring to play in European leagues. We handle everything from initial assessment and skill development to contract negotiations and cultural integration. Our team of experienced professionals works closely with each player to create personalized career pathways that maximize their potential and ensure sustainable success in European football.",
    keyFeatures: [
    "Personalized career development planning",
    "Professional skill assessment and training recommendations",
    "Strategic positioning with European clubs",
    "Contract negotiation and legal support",
    "Cultural integration and language training",
    "Ongoing performance monitoring and support"],

    deliverables: [
    "Comprehensive player profile and video portfolio",
    "Strategic career roadmap with milestone tracking",
    "Direct introductions to European club scouts",
    "Contract review and negotiation support",
    "Cultural adaptation program and resources",
    "Monthly performance reports and feedback sessions"],

    duration: "12-24 months",
    successRate: "87%",
    clientsServed: "250+",
    featured: true,
    complianceInfo: "All talent management activities are conducted in full compliance with FIFA Regulations on Working with Intermediaries (RIWI) and national football association guidelines."
  },
  {
    id: 2,
    title: "Transfer Facilitation",
    icon: "ArrowRightLeft",
    gradientClass: "bg-gradient-to-br from-secondary to-accent",
    description: "Expert transfer services ensuring FIFA-compliant processes, documentation management, and seamless transitions between African and European clubs.",
    fullDescription: "Our transfer facilitation service streamlines the complex process of international player transfers between African and European clubs. We manage all aspects of the transfer process including regulatory compliance, documentation, negotiations, and logistics. Our deep understanding of both African and European football markets ensures smooth, transparent, and legally compliant transfers that protect all parties involved.",
    keyFeatures: [
    "FIFA-compliant transfer documentation",
    "Multi-party negotiation management",
    "International Transfer Certificate (ITC) processing",
    "Visa and work permit assistance",
    "Financial transaction coordination",
    "Post-transfer integration support"],

    deliverables: [
    "Complete transfer documentation package",
    "Negotiated transfer agreement terms",
    "ITC application and processing",
    "Visa and work permit documentation",
    "Financial settlement coordination",
    "30-day post-transfer support period"],

    duration: "3-6 months",
    successRate: "92%",
    clientsServed: "180+",
    featured: false,
    complianceInfo: "All transfers are processed through FIFA's Transfer Matching System (TMS) ensuring full regulatory compliance and transparency."
  },
  {
    id: 3,
    title: "Performance Analytics",
    icon: "BarChart3",
    gradientClass: "bg-gradient-to-br from-accent to-primary",
    description: "Advanced data-driven insights using AI technology to evaluate player performance, identify strengths, and provide actionable improvement recommendations.",
    fullDescription: "Our performance analytics service leverages cutting-edge technology and data science to provide comprehensive player evaluations. We use advanced metrics, video analysis, and AI-powered insights to assess technical skills, tactical awareness, physical attributes, and psychological readiness. This data-driven approach helps players understand their current level and provides clear pathways for improvement.",
    keyFeatures: [
    "Comprehensive technical skill assessment",
    "Video analysis with AI-powered insights",
    "Physical performance metrics tracking",
    "Tactical awareness evaluation",
    "Psychological readiness assessment",
    "Benchmarking against European standards"],

    deliverables: [
    "Detailed performance analysis report",
    "Video breakdown with annotated insights",
    "Strength and development area identification",
    "Customized training recommendations",
    "Progress tracking dashboard access",
    "Quarterly reassessment and updates"],

    duration: "Ongoing",
    successRate: "95%",
    clientsServed: "320+",
    featured: false,
    complianceInfo: "All data collection and analysis adheres to GDPR and international data protection standards."
  },
  {
    id: 4,
    title: "Career Development",
    icon: "TrendingUp",
    gradientClass: "bg-gradient-to-br from-primary to-accent",
    description: "Holistic career planning including education support, financial literacy training, and long-term career sustainability programs for professional athletes.",
    fullDescription: "Our career development program takes a holistic approach to player success, recognizing that football careers are finite and require careful planning. We provide education support, financial literacy training, personal branding guidance, and post-career planning to ensure our clients build sustainable, successful lives both during and after their playing careers.",
    keyFeatures: [
    "Educational support and tutoring services",
    "Financial literacy and wealth management training",
    "Personal branding and media training",
    "Post-career planning and transition support",
    "Mental health and wellness programs",
    "Family support and integration services"],

    deliverables: [
    "Personalized career development plan",
    "Financial management workshops and resources",
    "Media training sessions and brand strategy",
    "Educational program enrollment support",
    "Mental wellness check-ins and counseling access",
    "Post-career transition roadmap"],

    duration: "Career-long",
    successRate: "89%",
    clientsServed: "200+",
    featured: false,
    complianceInfo: "All educational and financial services are provided by licensed professionals in accordance with local regulations."
  },
  {
    id: 5,
    title: "Legal & Compliance",
    icon: "Shield",
    gradientClass: "bg-gradient-to-br from-secondary to-primary",
    description: "Expert legal support ensuring full FIFA compliance, contract review, visa processing, and protection of player rights throughout the transfer process.",
    fullDescription: "Our legal and compliance service provides comprehensive legal support for all aspects of international football transfers and player representation. Our team of specialized sports lawyers ensures that all activities comply with FIFA regulations, national laws, and industry best practices. We protect player rights, review contracts, and handle all legal documentation required for international transfers.",
    keyFeatures: [
    "FIFA regulation compliance verification",
    "Contract review and negotiation support",
    "Visa and immigration legal assistance",
    "Dispute resolution and mediation services",
    "Intellectual property and image rights protection",
    "Regulatory filing and documentation management"],

    deliverables: [
    "Legal compliance audit and certification",
    "Reviewed and negotiated contract terms",
    "Visa application legal support",
    "Player rights protection documentation",
    "Dispute resolution services when needed",
    "Ongoing legal advisory access"],

    duration: "As needed",
    successRate: "98%",
    clientsServed: "280+",
    featured: false,
    complianceInfo: "Our legal team is licensed in multiple jurisdictions and maintains active FIFA intermediary licenses."
  },
  {
    id: 6,
    title: "Club Partnerships",
    icon: "Handshake",
    gradientClass: "bg-gradient-to-br from-accent to-secondary",
    description: "Strategic partnership development connecting European clubs with African talent pipelines, scouting networks, and collaborative development programs.",
    fullDescription: "Our club partnership service facilitates strategic relationships between European clubs and African football ecosystems. We help European clubs establish talent pipelines, scouting networks, and development partnerships that provide access to emerging African talent. These partnerships are structured to benefit all parties while ensuring player welfare and development remain the top priority.",
    keyFeatures: [
    "Strategic partnership structuring and negotiation",
    "Talent pipeline development and management",
    "Scouting network establishment in Africa",
    "Joint development program creation",
    "Cultural exchange and training initiatives",
    "Performance tracking and reporting systems"],

    deliverables: [
    "Partnership agreement and framework",
    "Talent identification and scouting protocols",
    "Joint development program curriculum",
    "Regular talent reports and recommendations",
    "Cultural exchange program coordination",
    "Quarterly partnership performance reviews"],

    duration: "Multi-year agreements",
    successRate: "85%",
    clientsServed: "45+ clubs",
    featured: true,
    complianceInfo: "All partnership agreements comply with FIFA solidarity mechanisms and training compensation regulations."
  }];


  const processSteps = [
  {
    title: "Initial Assessment",
    icon: "ClipboardCheck",
    description: "Comprehensive evaluation of player skills, potential, and career goals through detailed assessments and interviews.",
    details: [
    "Technical skill evaluation by certified coaches",
    "Physical fitness and medical screening",
    "Psychological readiness assessment",
    "Career goals and aspirations discussion",
    "Family situation and support system review"],

    duration: "2-3 weeks"
  },
  {
    title: "Profile Development",
    icon: "FileText",
    description: "Creation of professional player profiles including video highlights, performance data, and comprehensive documentation.",
    details: [
    "Professional video portfolio creation",
    "Statistical performance compilation",
    "Educational and personal background documentation",
    "Medical clearance and certification",
    "Digital profile optimization for club viewing"],

    duration: "3-4 weeks"
  },
  {
    title: "Market Positioning",
    icon: "Target",
    description: "Strategic positioning of players with appropriate European clubs based on skill level, playing style, and career objectives.",
    details: [
    "Target club identification and research",
    "Direct outreach to club scouts and technical directors",
    "Trial opportunity coordination",
    "Competitive positioning and differentiation",
    "Ongoing market intelligence gathering"],

    duration: "4-8 weeks"
  },
  {
    title: "Negotiation & Transfer",
    icon: "FileSignature",
    description: "Professional negotiation of contract terms, transfer fees, and all legal documentation required for international transfers.",
    details: [
    "Contract terms negotiation and review",
    "Transfer fee and payment structure agreement",
    "FIFA Transfer Matching System processing",
    "International Transfer Certificate application",
    "Visa and work permit processing"],

    duration: "4-6 weeks"
  },
  {
    title: "Integration & Support",
    icon: "Users",
    description: "Comprehensive support during transition including cultural adaptation, language training, and ongoing career management.",
    details: [
    "Relocation assistance and housing support",
    "Language and cultural orientation programs",
    "Financial management and banking setup",
    "Family integration support services",
    "Ongoing performance monitoring and career guidance"],

    duration: "Ongoing"
  }];


  const pricingPackages = [
  {
    name: "Essential",
    description: "Perfect for emerging talent seeking European opportunities",
    price: "€2,500",
    period: "one-time",
    features: [
    { text: "Initial player assessment and evaluation", included: true },
    { text: "Professional video portfolio creation", included: true },
    { text: "Basic profile distribution to 10 clubs", included: true },
    { text: "Contract review and basic legal support", included: true },
    { text: "3 months post-transfer support", included: true },
    { text: "Advanced analytics and performance tracking", included: false },
    { text: "Priority club introductions", included: false },
    { text: "Career development programs", included: false }],

    ctaText: "Get Started"
  },
  {
    name: "Professional",
    description: "Comprehensive service for serious career advancement",
    price: "€5,000",
    period: "one-time",
    popular: true,
    features: [
    { text: "Comprehensive player assessment and evaluation", included: true },
    { text: "Premium video portfolio with highlights", included: true },
    { text: "Profile distribution to 25+ targeted clubs", included: true },
    { text: "Full contract negotiation and legal support", included: true },
    { text: "6 months post-transfer support", included: true },
    { text: "Advanced analytics and performance tracking", included: true },
    { text: "Priority club introductions and trials", included: true },
    { text: "Basic career development programs", included: true }],

    ctaText: "Most Popular"
  },
  {
    name: "Elite",
    description: "Premium end-to-end career management service",
    price: "€10,000",
    period: "annual",
    features: [
    { text: "VIP player assessment and strategic planning", included: true },
    { text: "Professional video production and branding", included: true },
    { text: "Unlimited club introductions and networking", included: true },
    { text: "Expert contract negotiation and legal team", included: true },
    { text: "12 months comprehensive support", included: true },
    { text: "AI-powered analytics and insights", included: true },
    { text: "Exclusive club partnerships and opportunities", included: true },
    { text: "Full career development and education programs", included: true }],

    ctaText: "Go Elite"
  }];


  const testimonials = [
  {
    name: "Thomas Müller",
    role: "Technical Director",
    organization: "FC Bayern Munich II",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19ce4c35f-1763291872476.png",
    imageAlt: "Professional headshot of middle-aged German man with short blonde hair wearing dark blue suit and white shirt",
    quote: "ProX11Market has become our trusted partner for identifying African talent. Their thorough vetting process and FIFA-compliant approach gives us confidence in every player they present. We've successfully integrated three players from their network into our youth academy."
  },
  {
    name: "Kwame Mensah",
    role: "Professional Footballer",
    organization: "Currently at Sporting CP",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12bc42252-1763295294489.png",
    imageAlt: "Professional headshot of young African man with short black hair wearing red and white football jersey",
    quote: "ProX11Market changed my life. They handled everything from my transfer documentation to helping my family settle in Portugal. Their career development program taught me financial management and helped me adapt to European football culture. I'm forever grateful."
  },
  {
    name: "Sarah van der Berg",
    role: "Head Scout",
    organization: "Ajax Amsterdam",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10bc87545-1763300232979.png",
    imageAlt: "Professional headshot of middle-aged Dutch woman with shoulder-length brown hair wearing black blazer",
    quote: "The quality of players and the professionalism of ProX11Market's service is exceptional. Their performance analytics are incredibly detailed, and they understand both the African and European football landscapes. They've become an essential part of our scouting network."
  },
  {
    name: "Emmanuel Okocha",
    role: "Former Player, Now Coach",
    organization: "ProX11Market Alumni",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b2a1cf63-1763292736603.png",
    imageAlt: "Professional headshot of African man with short black hair wearing grey coaching jacket",
    quote: "Even after my playing career ended, ProX11Market supported my transition into coaching. Their post-career planning and education programs helped me obtain my UEFA coaching licenses. They truly care about long-term success, not just transfers."
  }];


  const faqs = [
  {
    question: "What makes ProX11Market different from other football agencies?",
    answer: "ProX11Market specializes exclusively in connecting African football talent with European opportunities. Our deep understanding of both markets, FIFA-compliant processes, comprehensive career development programs, and proven track record of successful placements set us apart. We're not just agents – we're career partners invested in long-term player success."
  },
  {
    question: "How long does the transfer process typically take?",
    answer: "The timeline varies depending on individual circumstances, but typically ranges from 3-6 months from initial assessment to completed transfer. This includes player evaluation, profile development, club negotiations, and all legal documentation. We work efficiently while ensuring all FIFA regulations and legal requirements are properly met."
  },
  {
    question: "What are the costs associated with your services?",
    answer: "We offer three main service packages ranging from €2,500 to €10,000 annually, depending on the level of service required. Our fees are transparent and competitive, with no hidden costs. We also offer customized packages for clubs and organizations. Payment plans are available for qualifying players."
  },
  {
    question: "Do you guarantee a transfer to a European club?",
    answer: "While we cannot guarantee transfers as they depend on multiple factors including player performance, club needs, and market conditions, we have an 87% success rate for players who complete our full program. We only accept players we believe have genuine potential for European football and work tirelessly to create opportunities."
  },
  {
    question: "What support do you provide after a player transfers to Europe?",
    answer: "Our support continues well after the transfer is complete. We provide cultural integration assistance, language training, financial management guidance, ongoing career counseling, and family support services. Depending on your package, post-transfer support ranges from 3 to 12 months, with options for extended career-long management."
  },
  {
    question: "Are your services FIFA compliant?",
    answer: "Absolutely. All our activities are conducted in full compliance with FIFA Regulations on Working with Intermediaries (RIWI), Transfer Matching System (TMS) requirements, and national football association guidelines. Our legal team maintains active FIFA intermediary licenses and ensures all transfers meet international standards."
  },
  {
    question: "What age groups do you work with?",
    answer: "We primarily work with players aged 16-28, though we evaluate each case individually. For players under 18, we have specialized programs that comply with FIFA's regulations on the protection of minors, including educational requirements and guardian involvement."
  },
  {
    question: "How do European clubs find players through your platform?",
    answer: "We maintain direct relationships with scouts and technical directors at over 150 European clubs. Players in our network have professional profiles accessible to our club partners, and we actively promote suitable players based on club needs. We also facilitate trial opportunities and arrange direct meetings between players and club representatives."
  }];


  const handleLearnMore = (service) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedService(null), 300);
  };

  const handleBookConsultation = () => {
    navigate('/contact');
  };

  const handleSelectPackage = (pkg) => {
    navigate('/contact', { state: { selectedPackage: pkg?.name } });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <section className="relative py-16 md:py-20 lg:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5" />
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzAwMCIgc3Ryb2tlLW9wYWNpdHk9IjAuMDMiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-40" />
          
          <div className="relative max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto mb-12 md:mb-16 lg:mb-20">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
                <Icon name="Briefcase" size={20} color="var(--color-primary)" />
                <span className="text-sm font-medium text-primary">Comprehensive Services</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-headline font-bold text-foreground mb-6">
                End-to-End Football Career Solutions
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                From talent identification to European placement, we provide comprehensive, FIFA-compliant services that transform African football dreams into European reality.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16 md:mb-20 lg:mb-24">
              {services?.map((service) =>
              <ServiceCard
                key={service?.id}
                service={service}
                onLearnMore={handleLearnMore} />

              )}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 lg:py-24 bg-muted">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <ProcessFlow steps={processSteps} />
          </div>
        </section>

        <section className="py-16 md:py-20 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <PricingSection
              packages={pricingPackages}
              onSelectPackage={handleSelectPackage} />

          </div>
        </section>

        <section className="py-16 md:py-20 lg:py-24 bg-muted">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <TestimonialCarousel testimonials={testimonials} />
          </div>
        </section>

        <section className="py-16 md:py-20 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <FAQSection faqs={faqs} />
          </div>
        </section>

        <section className="py-16 md:py-20 lg:py-24 bg-primary text-primary-foreground">
          <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 text-center">
            <Icon name="Calendar" size={64} className="mx-auto mb-6 opacity-90" />
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-headline font-bold mb-6">
              Ready to Start Your European Journey?
            </h2>
            <p className="text-lg md:text-xl mb-8 opacity-90">
              Book a free consultation with our expert team to discuss your career goals and explore how we can help you achieve them.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="secondary"
                size="lg"
                iconName="Calendar"
                iconPosition="left"
                onClick={handleBookConsultation}>

                Book Free Consultation
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Download"
                iconPosition="left"
                className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">

                Download Service Guide
              </Button>
            </div>
          </div>
        </section>
      </main>
      <footer className="bg-card border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Trophy" size={24} color="var(--color-primary)" />
              </div>
              <span className="text-lg font-headline font-bold text-foreground">ProX11Market</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date()?.getFullYear()} ProX11Market. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
      <ServiceModal
        service={selectedService}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onBookConsultation={handleBookConsultation} />

    </div>);

};

export default ServicesOverview;